import { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import styled from 'styled-components';
import { uploadImageToStorage, extractImageFromDataTransfer } from '../../../utils/uploadImage';

const EditorWrapper = styled.div`
  position: relative;

  .uploading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 6px;
  }

  .uploading-text {
    font-size: 14px;
    color: #007bff;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 8px;
`;

/**
 * 이미지 업로드 기능이 통합된 마크다운 에디터
 *
 * 사용법:
 * - 이미지를 드래그앤드롭하거나
 * - 클립보드에서 이미지를 붙여넣으면
 * - 자동으로 Firebase Storage에 업로드되고 마크다운으로 삽입됩니다
 */
const MarkdownEditor = ({
  value,
  onChange,
  height = 400,
  placeholder,
  folder = 'markdown-images'
}) => {
  const [uploading, setUploading] = useState(false);

  /**
   * 이미지를 마크다운 문법으로 삽입
   */
  const insertImage = (imageUrl, altText = 'image') => {
    const markdownImage = `![${altText}](${imageUrl})`;
    const newValue = value ? `${value}\n\n${markdownImage}` : markdownImage;
    onChange(newValue);
  };

  /**
   * 이미지 파일 업로드 처리
   */
  const handleImageUpload = async (file) => {
    try {
      setUploading(true);
      const imageUrl = await uploadImageToStorage(file, folder);
      insertImage(imageUrl, file.name);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert(`이미지 업로드 실패: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 붙여넣기 이벤트 핸들러
   */
  const handlePaste = async (event) => {
    const imageFile = extractImageFromDataTransfer(event.clipboardData);
    if (imageFile) {
      event.preventDefault();
      await handleImageUpload(imageFile);
    }
  };

  /**
   * 드래그앤드롭 이벤트 핸들러
   */
  const handleDrop = async (event) => {
    const imageFile = extractImageFromDataTransfer(event.dataTransfer);
    if (imageFile) {
      event.preventDefault();
      await handleImageUpload(imageFile);
    }
  };

  return (
    <EditorWrapper>
      <div
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <MDEditor
          value={value}
          onChange={onChange}
          height={height}
          preview="live"
          textareaProps={{
            placeholder: placeholder || '내용을 작성하세요...\n\n💡 이미지를 드래그앤드롭하거나 붙여넣으면 자동으로 업로드됩니다.'
          }}
        />
      </div>

      {uploading && (
        <div className="uploading-overlay">
          <div className="uploading-text">이미지 업로드 중...</div>
        </div>
      )}

      <HelpText>
        💡 툴바를 사용하거나 마크다운 문법으로 작성할 수 있습니다.
        이미지는 드래그앤드롭 또는 붙여넣기로 추가하세요.
      </HelpText>
    </EditorWrapper>
  );
};

export default MarkdownEditor;
